import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Hash, PoundSterling, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import type { Asset } from '@/types/asset';
import { DateInputPicker } from '@/components/ui/date-input-picker';
import { useLanguageSettings } from '@/contexts/LanguageSettingsContext';

interface AssetDetailCustomFieldsProps {
  readonly asset: Asset;
  readonly onAssetUpdate: (asset: Asset) => void;
  readonly isEditMode?: boolean;
}

export function AssetDetailCustomFields({ asset, onAssetUpdate, isEditMode = false }: Readonly<AssetDetailCustomFieldsProps>) {
  const { getLabel } = useLanguageSettings();
  
  const handleFieldChange = (field: keyof Asset, value: any) => {
    onAssetUpdate({
      ...asset,
      [field]: value,
    });
  };

  const handleDateChange = (field: keyof Asset, date: Date | undefined) => {
    handleFieldChange(field, date ? date.toISOString() : undefined);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* User Key Fields */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5" />
            User Key Fields
          </CardTitle>
          <CardDescription className="text-xs">
            Custom text fields for additional asset identification
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4, 5].map((num) => {
              const fieldKey = `userKey${num}` as keyof Asset;
              const value = asset[fieldKey] as string | undefined;
              const label = getLabel(`userKey${num}`);
              
              return (
                <div key={fieldKey} className="space-y-2">
                  <Label htmlFor={fieldKey}>{label}</Label>
                  {isEditMode ? (
                    <Input
                      id={fieldKey}
                      value={value ?? ''}
                      onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                      placeholder={`Enter ${label.toLowerCase()}`}
                    />
                  ) : (
                    <Input
                      id={fieldKey}
                      value={value ?? ''}
                      readOnly
                    />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* User Value Fields */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PoundSterling className="h-5 w-5" />
            User Value Fields
          </CardTitle>
          <CardDescription className="text-xs">
            Custom numeric fields for additional asset values
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4, 5].map((num) => {
              const fieldKey = `userValue${num}` as keyof Asset;
              const value = asset[fieldKey] as number | undefined;
              const label = getLabel(`userValue${num}`);
              
              return (
                <div key={fieldKey} className="space-y-2">
                  <Label htmlFor={fieldKey}>{label}</Label>
                  {isEditMode ? (
                    <Input
                      id={fieldKey}
                      type="number"
                      value={value ?? ''}
                      onChange={(e) => handleFieldChange(fieldKey, e.target.value ? parseFloat(e.target.value) : undefined)}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      step="0.01"
                    />
                  ) : (
                    <Input
                      id={fieldKey}
                      value={value !== undefined && value !== null 
                        ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: asset.currency ?? 'GBP' }).format(value)
                        : ''
                      }
                      readOnly
                    />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* User Date Fields */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            User Date Fields
          </CardTitle>
          <CardDescription className="text-xs">
            Custom date fields for tracking important milestones
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3].map((num) => {
              const fieldKey = `userDate${num}` as keyof Asset;
              const value = asset[fieldKey] as string | undefined;
              const dateValue = value ? new Date(value) : undefined;
              const label = getLabel(`userDate${num}`);
              
              return (
                <div key={fieldKey} className="space-y-2">
                  <Label htmlFor={fieldKey}>{label}</Label>
                  {isEditMode ? (
                    <DateInputPicker
                      date={dateValue}
                      onDateChange={(date) => handleDateChange(fieldKey, date)}
                      placeholder="DD/MM/YYYY"
                    />
                  ) : (
                    <Input
                      id={fieldKey}
                      value={dateValue ? format(dateValue, 'dd/MM/yyyy') : ''}
                      readOnly
                    />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
